/**
 * CoffeeMates Groups Service
 */
import httpClient from '../utils/httpClient';

export interface BoardAnswer {
  user_id: string;
  first_name: string;
  last_name_initial: string;
  avatar_url: string | null;
  answer: string;
  is_mine: boolean;
  created_at: string | null;
}

export interface BoardQuestion {
  question_id: string;
  question_text: string;
  answers: BoardAnswer[];
  my_answer: string | null;
}

export interface BoardResponse {
  questions: BoardQuestion[];
}

export interface GroupMember {
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  bio: string | null;
  industry: string | null;
}

export interface GroupDetail {
  id: string;
  group_number: number;
  telegram_link: string | null;
  status: string;
  member_count: number;
  event: {
    id: string;
    title: string;
    event_type: string;
    scheduled_date: string;
    scheduled_time: string | null;
    duration_minutes: number;
    topic_brief: string | null;
    game_activity: string | null;
  } | null;
  venue: {
    id: string;
    name: string;
    address: string;
    photo_url: string | null;
    location_lat: number | null;
    location_lng: number | null;
  } | null;
  members: GroupMember[];
}

const groupsService = {
  async getCurrent(): Promise<{ group: GroupDetail | null; message?: string }> {
    const { data } = await httpClient.get('/groups/current');
    return data;
  },

  async getById(groupId: string): Promise<unknown> {
    const { data } = await httpClient.get(`/groups/${groupId}`);
    return data;
  },

  async generateJoinCode(eventId: string): Promise<{ join_code: string; message: string }> {
    const { data } = await httpClient.post(`/groups/join-together?event_id=${encodeURIComponent(eventId)}`);
    return data;
  },

  async useJoinCode(eventId: string, code: string): Promise<unknown> {
    const { data } = await httpClient.post(`/groups/join-together?event_id=${encodeURIComponent(eventId)}&friend_code=${encodeURIComponent(code)}`);
    return data;
  },

  // ── Group Board ──────────────────────────────────────────────────────
  async getQuestions(): Promise<{ id: string; text: string }[]> {
    const { data } = await httpClient.get('/groups/questions');
    return data;
  },

  async getBoard(groupId: string): Promise<BoardResponse> {
    const { data } = await httpClient.get(`/groups/${groupId}/board`);
    return data;
  },

  async postAnswer(groupId: string, questionId: string, answer: string): Promise<unknown> {
    const { data } = await httpClient.post(`/groups/${groupId}/board`, { question_id: questionId, answer });
    return data;
  },
};

export default groupsService;
